/**
 * SessionExpiredModal.jsx
 * Aparece quando a sessão do Firebase expira enquanto o usuário está
 * com algum formulário aberto. Permite re-autenticar sem navegar,
 * preservando todo o estado da tela atual.
 */
import React, { useState } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  Button, Input, InputGroup, InputRightElement,
  Icon, Text, Box, VStack, Flex, Divider,
} from '@chakra-ui/react';
import { MdOutlineRemoveRedEye, MdLockOutline } from 'react-icons/md';
import { RiEyeCloseLine } from 'react-icons/ri';
import { FcGoogle } from 'react-icons/fc';
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
} from 'firebase/auth';
import { auth } from '../firebase';
import { remoteLog } from '../utils/remoteLog';

function firebaseMsg(err) {
  const map = {
    'auth/wrong-password':         'Senha incorreta.',
    'auth/invalid-credential':     'E-mail ou senha incorretos.',
    'auth/too-many-requests':      'Muitas tentativas. Aguarde um momento.',
    'auth/network-request-failed': 'Sem conexão. Verifique sua internet.',
    'auth/user-disabled':          'Conta desativada.',
  };
  return map[err?.code] || 'Falha ao autenticar. Tente novamente.';
}

export default function SessionExpiredModal({ isOpen, userEmail, onSuccess, onSignOut }) {
  const [password,      setPassword]      = useState('');
  const [showPw,        setShowPw]        = useState(false);
  const [loading,       setLoading]       = useState(false);
  const [loadingGoogle, setLoadingGoogle] = useState(false);
  const [error,         setError]         = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoading(true);
    setError('');
    try {
      await signInWithEmailAndPassword(auth, userEmail, password);
      setPassword('');
      setError('');
      remoteLog('info', 'session_modal_reauth_email_ok', 'Re-auth por e-mail OK', { email: userEmail });
      onSuccess();
    } catch (err) {
      remoteLog('warn', 'session_modal_reauth_email_fail', `Falha re-auth e-mail: ${err?.code}`, { code: err?.code, email: userEmail });
      setError(firebaseMsg(err));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setLoadingGoogle(true);
    setError('');
    try {
      await signInWithPopup(auth, new GoogleAuthProvider());
      setError('');
      remoteLog('info', 'session_modal_reauth_google_ok', 'Re-auth Google OK', {});
      onSuccess();
    } catch (err) {
      remoteLog('warn', 'session_modal_reauth_google_fail', `Falha re-auth Google: ${err?.code}`, { code: err?.code });
      setError(firebaseMsg(err));
    } finally {
      setLoadingGoogle(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {}}
      isCentered
      closeOnOverlayClick={false}
      closeOnEsc={false}
      size="sm"
    >
      <ModalOverlay bg="blackAlpha.800" backdropFilter="blur(3px)" />
      <ModalContent mx="16px" borderRadius="16px">

        <ModalHeader pb="2px">
          <Flex align="center" gap="8px" mb="2px">
            <Icon as={MdLockOutline} boxSize="20px" color="orange.400" />
            <Text fontSize="md" fontWeight="700">Sessão expirada</Text>
          </Flex>
          <Text fontSize="sm" color="gray.500" fontWeight="normal">
            Faça login novamente para continuar&nbsp;
            <Text as="span" fontWeight="600" color="brand.500">de onde parou.</Text>
            &nbsp;Seus dados preenchidos serão mantidos.
          </Text>
        </ModalHeader>

        <ModalBody pt="16px" pb="8px">
          <VStack spacing="12px" as="form" onSubmit={handleSubmit}>

            {/* E-mail read-only */}
            <Box w="100%">
              <Text fontSize="11px" fontWeight="700" textTransform="uppercase"
                color="gray.500" mb="4px" letterSpacing=".04em">
                E-mail
              </Text>
              <Input
                value={userEmail || ''}
                isReadOnly
                size="sm"
                borderRadius="8px"
                bg="gray.100"
                _dark={{ bg: 'navy.700', color: 'gray.300' }}
                color="gray.600"
                cursor="default"
              />
            </Box>

            {/* Senha */}
            <Box w="100%">
              <Text fontSize="11px" fontWeight="700" textTransform="uppercase"
                color="gray.500" mb="4px" letterSpacing=".04em">
                Senha
              </Text>
              <InputGroup size="sm">
                <Input
                  type={showPw ? 'text' : 'password'}
                  placeholder="Sua senha"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError(''); }}
                  borderRadius="8px"
                  autoFocus
                  autoComplete="current-password"
                />
                <InputRightElement>
                  <Icon
                    as={showPw ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                    cursor="pointer"
                    color="gray.400"
                    onClick={() => setShowPw(v => !v)}
                  />
                </InputRightElement>
              </InputGroup>
            </Box>

            {error && (
              <Text fontSize="12px" color="red.500" w="100%" mt="-4px">
                {error}
              </Text>
            )}

            <Button
              type="submit"
              colorScheme="brand"
              w="100%"
              size="sm"
              borderRadius="8px"
              isLoading={loading}
              isDisabled={!password || loadingGoogle}
            >
              Entrar e continuar
            </Button>
          </VStack>

          <Flex align="center" my="14px" gap="8px">
            <Divider />
            <Text fontSize="11px" color="gray.400" flexShrink="0">ou</Text>
            <Divider />
          </Flex>

          <Button
            w="100%"
            size="sm"
            borderRadius="8px"
            leftIcon={<Icon as={FcGoogle} boxSize="16px" />}
            variant="outline"
            onClick={handleGoogle}
            isLoading={loadingGoogle}
            isDisabled={loading}
          >
            Entrar com Google
          </Button>
        </ModalBody>

        <ModalFooter pt="4px" pb="12px" justifyContent="center">
          <Button
            variant="ghost"
            colorScheme="red"
            size="xs"
            onClick={onSignOut}
            isDisabled={loading || loadingGoogle}
          >
            Sair e descartar dados
          </Button>
        </ModalFooter>

      </ModalContent>
    </Modal>
  );
}
